import React, { useState } from 'react';
import { ChevronDown, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Assuming Reviewer type is defined elsewhere or passed in
// For standalone use, define it here:
interface Reviewer {
  id: string;
  firstName: string;
  lastName: string;
  photoURL?: string | null;
}

interface ReviewerDropdownProps {
  reviewers: Reviewer[];
  selectedReviewerId: string | null;
  onReviewerSelect: (reviewerId: string | null) => void;
  className?: string;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string; // Optional placeholder text
  currentUserId?: string | null; // Added prop
}

export const ReviewerDropdown: React.FC<ReviewerDropdownProps> = ({
  reviewers = [],
  selectedReviewerId = null,
  onReviewerSelect,
  className = '',
  isLoading = false,
  disabled = false,
  placeholder,
  currentUserId = null, // Added prop
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  
  // Find the selected reviewer object
  const selectedReviewer = reviewers.find(r => r?.id === selectedReviewerId) || null;
  const displayPlaceholder = placeholder || t('cards.approval.selectPlaceholder', 'Select a reviewer...');

  // Show skeleton loader during loading
  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg animate-pulse ${className}`}>
        <div className="w-5 h-5 rounded-full bg-gray-200"></div>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <ChevronDown className="w-4 h-4 text-gray-300" />
      </div>
    );
  }

  // Handle no reviewers available state
  if (reviewers.length === 0 && !isLoading) {
      return (
        <div className={`flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-400 ${className}`}>
            <User className="w-5 h-5 text-gray-400" />
            <span className="text-sm truncate">{t('cards.approval.noReviewers', 'No reviewers available')}</span>
            <ChevronDown className="w-4 h-4 text-gray-300" />
        </div>
    );
  }

  const handleImageError = (reviewerId: string) => {
    setImageErrors(prev => ({
      ...prev,
      [reviewerId]: true
    }));
  };

  // Determine button content (selected reviewer or placeholder)
  const ButtonContent = () => {
      if (selectedReviewer) {
          return (
              <>
                  {selectedReviewer.photoURL && !imageErrors[selectedReviewer.id] ? (
                      <img 
                          src={selectedReviewer.photoURL} 
                          alt={`${selectedReviewer.firstName} ${selectedReviewer.lastName}`}
                          className="w-5 h-5 rounded-full flex-shrink-0 object-cover"
                          onError={() => handleImageError(selectedReviewer.id)}
                      />
                  ) : (
                      <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 flex-shrink-0">
                          {selectedReviewer.firstName?.charAt(0)}{selectedReviewer.lastName?.charAt(0)}
                      </span>
                  )}
                  <span className="text-sm text-gray-700 truncate">
                      {`${selectedReviewer.firstName} ${selectedReviewer.lastName}`}
                  </span>
              </>
          );
      } else {
          return (
              <span className="text-sm text-gray-500 truncate">
                  {displayPlaceholder}
              </span>
          );
      }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)} 
        disabled={disabled || reviewers.length === 0} // Also disable if no reviewers
        className={`w-full flex items-center justify-between gap-2 px-4 py-2 border border-gray-200 rounded-lg transition-colors h-10 ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-primary-300'} ${className}`}
      >
        <span className="flex items-center gap-2 overflow-hidden">
          <ButtonContent />
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
      </button>

      {isOpen && !disabled && (
        <>
          <div 
            className="fixed inset-0 z-10 bg-transparent" // Make overlay transparent or styled as needed
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 mt-1 w-full rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20 max-h-60 overflow-y-auto"> {/* Added max-height and overflow */}
            <div className="py-1">
              {reviewers
                .filter(reviewer => reviewer.id !== currentUserId || reviewer.id === selectedReviewerId) // Filter out current user unless they are selected
                .map(reviewer => (
                <button
                  key={reviewer.id}
                  type="button"
                  onClick={() => {
                    onReviewerSelect(reviewer.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left ${ 
                    reviewer.id === selectedReviewerId
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {reviewer.photoURL && !imageErrors[reviewer.id] ? (
                    <img 
                      src={reviewer.photoURL} 
                      alt={`${reviewer.firstName} ${reviewer.lastName}`}
                      className="w-5 h-5 rounded-full flex-shrink-0 object-cover"
                      onError={() => handleImageError(reviewer.id)}
                    />
                  ) : (
                     <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 flex-shrink-0">
                        {reviewer.firstName?.charAt(0)}{reviewer.lastName?.charAt(0)}
                    </span>
                  )}
                  <span className="truncate">{`${reviewer.firstName} ${reviewer.lastName}`}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}; 